import {
  UserDto,
  KnownUserStatus,
  ProjectDto,
  KnownUserDtoUserType,
  EditCustomerDto,
  EditOccupantDto,
  CreateCustomerDto,
  CreateOccupantDto,
  EditUserDto,
  ProjectModuleSettingsDto,
  EntitySettingsDto,
  KnownDeliveryDtoDeliveryType
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { HighlightOff } from '@mui/icons-material';
import _ from 'lodash';
import { convertHyphenated } from '../../../../core/util/string-conversion';
import { UserDetailsPageHeaderProps } from '../../../../ui/components/pure/page-headers/details/user-details/user-details-page-header.component';
import { Colors } from '../../../../ui/theme/colors';
import { deliveryTypeToIconMap } from '../../../deliveries/maps/delivery-type-to-icon.map';
import { mapProjectModuleToExternalDeliveryType } from '../../../project-modules/mappings';
import { getUserCapabilitiesFromProjectModules } from '../../logic/user-capabilities.map';
import { CreateOrEditUserFormState } from '../actions/edit-user/edit-user-action.hook';
import { UserDetailsActions, makeDefineUserDetailsActions } from '../actions/user-details.actions';

export function makeUserDetailsHeaderProps(
  user: UserDto,
  retailer: ProjectDto,
  projectModules: {
    id: string;
    data: ProjectModuleSettingsDto;
    schema: EntitySettingsDto['schema'];
  }[],
  setAction: (action: UserDetailsActions) => void,
  userLabel: string
): UserDetailsPageHeaderProps {
  const { explicitModules, implicitModules } = getUserCapabilitiesFromProjectModules(
    user.projectModules,
    projectModules
  );

  const deliveryTypesForProject = implicitModules
    .map((m) => mapProjectModuleToExternalDeliveryType(m))
    .filter((type): type is KnownDeliveryDtoDeliveryType => type !== null);

  const deliveryTypesForUser = explicitModules
    .map((m) => mapProjectModuleToExternalDeliveryType(m))
    .filter((type): type is KnownDeliveryDtoDeliveryType => type !== null);

  return {
    name: user.displayName || userLabel,
    groupName: user.groupName,
    ...(user.status === KnownUserStatus.Active
      ? false
      : {
          status: {
            label: user.status,
            color: Colors.cancelledDeletedRed,
            Icon: HighlightOff
          }
        }),
    actions: makeDefineUserDetailsActions(user.userType as KnownUserDtoUserType, user)(setAction),
    email: user.email,
    mobile: user.mobile,
    ...(retailer.address && {
      address: projectAddressToProps(retailer.address)
    }),
    prefersLowerLockers: user.prefersLowerLockers,
    prefersAudioPrompts: user.prefersAudioPrompts,
    managedByAccount: user.managedByAccount,
    externalID: user.externalID,
    transactionTypesForUser: [...deliveryTypesForUser, ...deliveryTypesForProject].map((type) => {
      return {
        Icon: deliveryTypeToIconMap[type],
        label: convertHyphenated(type, { capitalizeEachWord: true })
      };
    })
  };
}

function projectAddressToProps(address: ProjectDto['address']): UserDetailsPageHeaderProps['address'] {
  const line1 = (address?.line1 ? address.line1 + ' ' : '') + (address?.line2 || '');
  const line2 =
    (address?.suburb ? address.suburb + ' ' : '') +
    (address?.postcode ? address.postcode + ' ' : '') +
    (address?.state || '');
  return { line1, line2 };
}

export function mapUserDtoToUserForm(projectID: string, userDto: UserDto): CreateOrEditUserFormState {
  return {
    email: userDto.email,
    externalID: userDto.externalID,
    firstName: userDto.firstName,
    lastName: userDto.lastName,
    groupName: userDto.groupName,
    maidenName: userDto.maidenName,
    mobile: userDto.mobile,
    preferredName: userDto.preferredName,
    prefersLowerLockers: userDto.prefersLowerLockers,
    prefersAudioPrompts: userDto.prefersAudioPrompts,
    projectID
  };
}

export function mapUserFormToCreateCustomerDto(form: CreateOrEditUserFormState): CreateCustomerDto {
  return {
    firstName: form.firstName,
    prefersLowerLockers: form.prefersLowerLockers,
    ..._.omitBy(
      {
        externalID: form.externalID,
        groupName: form.groupName,
        lastName: form.lastName,
        maidenName: form.maidenName,
        email: form.email,
        mobile: form.mobile,
        preferredName: form.preferredName
      },
      (value) => value === ''
    )
  };
}

export function mapUserFormToEditCustomerDto(form: CreateOrEditUserFormState, userDto: UserDto): EditCustomerDto {
  const editDto: EditCustomerDto = {
    email: form.email || null,
    externalID: form.externalID || null,
    firstName: form.firstName,
    groupName: form.groupName,
    lastName: form.lastName || null,
    maidenName: form.maidenName || null,
    mobile: form.mobile || null,
    preferredName: form.preferredName || null,
    prefersLowerLockers: form.prefersLowerLockers
  };

  return cleanupEditDto(editDto, userDto);
}

export function mapUserFormToCreateOccupantDto(form: CreateOrEditUserFormState): CreateOccupantDto {
  return {
    projectID: form.projectID,
    firstName: form.firstName,
    unitName: form.groupName,
    prefersLowerLockers: form.prefersLowerLockers,
    prefersAudioPrompts: form.prefersAudioPrompts,
    ..._.omitBy(
      {
        externalID: form.externalID,
        lastName: form.lastName,
        maidenName: form.maidenName,
        mobile: form.mobile,
        email: form.email,
        preferredName: form.preferredName
      },
      (value) => value === ''
    )
  };
}

export function mapUserFormToEditOccupantDto(form: CreateOrEditUserFormState, userDto: UserDto): EditOccupantDto {
  const editDto: EditOccupantDto = {
    email: form.email || null,
    externalID: form.externalID || null,
    firstName: form.firstName,
    unitName: form.groupName,
    lastName: form.lastName || null,
    maidenName: form.maidenName || null,
    mobile: form.mobile || null,
    preferredName: form.preferredName || null,
    prefersLowerLockers: form.prefersLowerLockers,
    prefersAudioPrompts: form.prefersAudioPrompts
  };

  return cleanupEditDto(editDto, userDto);
}

export function mapUserFormToEditUserDto(form: CreateOrEditUserFormState, userDto: UserDto): EditUserDto {
  const editDto: EditUserDto = {
    email: form.email || null,
    externalID: form.externalID || null,
    firstName: form.firstName,
    lastName: form.lastName || null,
    maidenName: form.maidenName || null,
    mobile: form.mobile || null,
    preferredName: form.preferredName || null,
    prefersLowerLockers: form.prefersLowerLockers,
    prefersAudioPrompts: form.prefersAudioPrompts
  };

  return cleanupEditDto(editDto, userDto);
}

// This method removes any properties whose values are:
// - is a key with a different name that has not changed (e.g. unitName)
// - an empty string
// - the same as the original DTO
// - null but not present in the original DTO
function cleanupEditDto<T extends Record<string, any>, U extends T>(editDto: T, dto: U) {
  return _.omitBy(
    editDto,
    (value, key) =>
      (key === 'unitName' && value === dto['groupName']) ||
      value === '' ||
      dto[key as keyof T] === value ||
      (value === null && dto[key as keyof T] === undefined)
  );
}
